import Vue from 'vue';
import {emptyListResponse} from '@/domain/core/utils/django';

export default (service) => ({
    namespaced: true,

    state: {
        scope: null, // {type: 'global' | 'contract-manager' | 'pica-service', id: number}
        collaborators: {...emptyListResponse},
        availableRoles: [],
        loading: {
            collaborators: false,
            roles: false,
            update: false
        },
        filters: {
            status: null,
            search: '',
            page: 1,
            pageSize: 20
        },
        selectedCollaborator: null,
        collaboratorPermissions: null,
        // Virtual Users
        virtualUserCredentials: null, // Stores credentials after create/reset (shown only once)
        // Collaborator Limits
        limitsUsage: null // {configured: bool, role_limits: {...}, total_limit: {...}, can_bypass: bool}
    },
    
    getters: {
        hasScope: (state) => state.scope !== null,
        
        collaborators: (state) => state.collaborators,
        
        availableRoles: (state) => state.availableRoles || [],

        inheritedCollaborators: (state) => (state.collaborators.results || [])
            .filter(c => (c.roles || []).every(r => r.is_inherited)),

        directCollaborators: (state) => (state.collaborators.results || [])
            .filter(c => (c.roles || []).some(r => !r.is_inherited)),

        pendingInvitations: (state) => (state.collaborators.results || [])
            .filter(c => c.status === 'PENDING'),

        activeCollaborators: (state) => (state.collaborators.results || [])
            .filter(c => c.status === 'ACCEPTED'),

        disabledCollaborators: (state) => (state.collaborators.results || [])
            .filter(c => c.status === 'DISABLED'),

        collaboratorPermissions: (state) => state.collaboratorPermissions,

        // Virtual Users Getters
        virtualUsers: (state) => (state.collaborators.results || [])
            .filter(c => c.is_virtual === true),

        hasVirtualUsers: (state, getters) => getters.virtualUsers.length > 0,

        virtualUserCount: (state, getters) => getters.virtualUsers.length,

        virtualUserCredentials: (state) => state.virtualUserCredentials,

        // Collaborator Limits Getters
        limitsUsage: (state) => state.limitsUsage,

        hasLimitsConfigured: (state) => state.limitsUsage?.configured === true,

        canBypassLimits: (state) => state.limitsUsage?.can_bypass === true,

        allRoleLimitsExhausted: (state) => {
            const usage = state.limitsUsage
            if (!usage?.configured) return false
            if (usage.can_bypass) return false

            // Check total limit
            if (usage.total_limit && usage.total_limit.available <= 0) return true

            // Check per-role limits
            const roleLimits = usage.role_limits
            if (!roleLimits || Object.keys(roleLimits).length === 0) return false

            return Object.values(roleLimits).every(rl => rl.available <= 0)
        },

        totalCollaboratorCount: (state) => {
            return state.collaborators?.pagination?.total || state.collaborators?.results?.length || 0
        }
    },
    
    mutations: {
        setScope(state, scope) {
            Vue.set(state, 'scope', scope);
        },
        
        setCollaborators(state, data) {
            Vue.set(state, 'collaborators', data);
        },
        
        setAvailableRoles(state, roles) {
            Vue.set(state, 'availableRoles', roles);
        },
        
        setLoading(state, { key, value }) {
            Vue.set(state.loading, key, value);
        },
        
        setFilters(state, filters) {
            Object.keys(filters).forEach(key => {
                if (Object.prototype.hasOwnProperty.call(state.filters, key)) {
                    Vue.set(state.filters, key, filters[key]);
                }
            });
        },
        
        resetFilters(state) {
            Vue.set(state, 'filters', {
                status: null,
                search: '',
                page: 1,
                pageSize: 20
            });
        },

        setSelectedCollaborator(state, collaborator) {
            Vue.set(state, 'selectedCollaborator', collaborator);
        },
        
        setCollaboratorPermissions(state, permissions) {
            Vue.set(state, 'collaboratorPermissions', permissions);
        },
        
        updateCollaboratorInList(state, updatedCollaborator) {
            const index = state.collaborators.results.findIndex(
                c => c.id === updatedCollaborator.id
            );
            if (index !== -1) {
                Vue.set(state.collaborators.results, index, updatedCollaborator);
            }
        },
        
        removeCollaboratorFromList(state, collaboratorId) {
            const index = state.collaborators.results.findIndex(
                c => c.id === collaboratorId
            );
            if (index !== -1) {
                state.collaborators.results.splice(index, 1);
            }
        },

        // Virtual Users Mutations
        setVirtualUserCredentials(state, credentials) {
            Vue.set(state, 'virtualUserCredentials', credentials);
        },

        clearVirtualUserCredentials(state) {
            Vue.set(state, 'virtualUserCredentials', null);
        },

        // Collaborator Limits Mutations
        setLimitsUsage(state, usage) {
            Vue.set(state, 'limitsUsage', usage);
        },

        clearLimitsUsage(state) {
            Vue.set(state, 'limitsUsage', null);
        }
    },
    
    actions: {
        async setScope({ commit, dispatch }, scope) {
            commit('setScope', scope);
            // Fetch data for new scope
            try {
                await Promise.all([
                    dispatch('fetchCollaborators'),
                    dispatch('fetchAvailableRoles')
                ]);
            } catch (error) {
                // Don't re-throw to allow the component to render
            }
        },
        
        async fetchCollaborators({ commit, state }) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }
            
            commit('setLoading', { key: 'collaborators', value: true });
            
            try {
                const data = await service.fetchCollaborators(state.scope, {
                    status: state.filters.status,
                    search: state.filters.search,
                    page: state.filters.page,
                    pageSize: state.filters.pageSize
                });
                
                // Mappa page_number a page per compatibilità con AjaxTable
                if (data && data.pagination && data.pagination.page_number) {
                    data.pagination.page = data.pagination.page_number;
                }
                
                // Il service restituisce direttamente i dati, non response.data
                commit('setCollaborators', data || {...emptyListResponse});
            } catch (error) {
                commit('setCollaborators', {...emptyListResponse}); // Set empty list on error
                throw error;
            } finally {
                commit('setLoading', { key: 'collaborators', value: false });
            }
        },
        
        async fetchAvailableRoles({ commit, state }, params = {}) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }
            
            commit('setLoading', { key: 'roles', value: true });
            
            try {
                const data = await service.fetchAvailableRoles(state.scope, params);
                // Il service restituisce direttamente i dati
                // Se è un oggetto con results, usa results, altrimenti usa direttamente data
                const roles = data?.results || data || [];
                commit('setAvailableRoles', Array.isArray(roles) ? roles : []);
            } catch (error) {
                commit('setAvailableRoles', []); // Set empty array on error
                throw error;
            } finally {
                commit('setLoading', { key: 'roles', value: false });
            }
        },
        
        async inviteCollaborators({ state, dispatch }, data) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }

            try {
                const result = await service.inviteCollaborators(state.scope, data);
                await dispatch('fetchCollaborators');
                await dispatch('fetchLimitsUsage');
                return result; // Ritorna success/errors per gestione nel componente
            } catch (error) {
                throw error;
            }
        },

        async assignCollaborators({ state, dispatch }, data) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }

            try {
                const result = await service.assignCollaborators(state.scope, data);
                await dispatch('fetchCollaborators');
                await dispatch('fetchLimitsUsage');
                return result; // Ritorna success/errors per gestione nel componente
            } catch (error) {
                throw error;
            }
        },
        
        async searchUsers({ state }, { searchTerm, excludeExisting = true }) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }
            
            try {
                const data = await service.searchUsers(state.scope, searchTerm);
                // Filtra utenti già collaboratori se richiesto
                if (excludeExisting && data?.results) {
                    return data.results.filter(user => !user.already_collaborator);
                }
                return data?.results || [];
            } catch (error) {
                return [];
            }
        },
        
        async resendInvitation({ state }, collaboratorId) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }
            
            try {
                await service.resendInvitation(state.scope, collaboratorId);
                // No need to refresh - just sending an email
            } catch (error) {
                throw error;
            }
        },
        
        async updateCollaboratorEmail({ state, dispatch }, { collaboratorId, email }) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }
            
            try {
                await service.updateCollaboratorEmail(state.scope, collaboratorId, email);
                await dispatch('fetchCollaborators');
            } catch (error) {
                throw error;
            }
        },
        
        async forceAcceptInvitation({ state, dispatch }, collaboratorId) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }
            
            try {
                await service.forceAcceptInvitation(state.scope, collaboratorId);
                await dispatch('fetchCollaborators');
            } catch (error) {
                throw error;
            }
        },
        
        async fetchCollaboratorDetail({ state }, collaboratorId) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }

            try {
                return await service.getCollaboratorDetail(state.scope, collaboratorId);
            } catch (error) {
                throw error;
            }
        },

        async fetchCollaboratorPermissions({ state, commit }, collaboratorId) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }

            try {
                const data = await service.getCollaboratorPermissions(state.scope, collaboratorId);
                commit('setCollaboratorPermissions', data);
                return data;
            } catch (error) {
                throw error;
            }
        },
        
        async fetchRolePermissions({ state }, roleId) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }
            
            try {
                return await service.getRolePermissions(state.scope, roleId);
            } catch (error) {
                throw error;
            }
        },
        
        updateFilters({ commit, dispatch }, filters) {
            commit('setFilters', filters);
            if (filters.page === undefined) {
                commit('setFilters', { page: 1 });
            }
            return dispatch('fetchCollaborators');
        },
        
        changePage({ commit, dispatch }, page) {
            commit('setFilters', { page });
            return dispatch('fetchCollaborators');
        },
        
        async disableRoleForCollaborator({ state, dispatch, commit }, { collaboratorId, roleId }) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }

            commit('setLoading', { key: 'update', value: true });

            try {
                await service.disableCollaboratorRole(state.scope, collaboratorId, roleId);
                await dispatch('fetchCollaborators');
                await dispatch('fetchLimitsUsage');
            } catch (error) {
                throw error;
            } finally {
                commit('setLoading', { key: 'update', value: false });
            }
        },

        async enableRoleForCollaborator({ state, dispatch, commit }, { collaboratorId, roleId }) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }

            commit('setLoading', { key: 'update', value: true });

            try {
                await service.enableCollaboratorRole(state.scope, collaboratorId, roleId);
                await dispatch('fetchCollaborators');
                await dispatch('fetchLimitsUsage');
            } catch (error) {
                throw error;
            } finally {
                commit('setLoading', { key: 'update', value: false });
            }
        },

        async assignRoleToCollaborator({ state, dispatch, commit }, { collaboratorId, roleId }) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }

            commit('setLoading', { key: 'update', value: true });

            try {
                await service.addCollaboratorRole(state.scope, collaboratorId, roleId);
                await dispatch('fetchCollaborators');
                await dispatch('fetchLimitsUsage');
            } catch (error) {
                throw error;
            } finally {
                commit('setLoading', { key: 'update', value: false });
            }
        },

        async removeRoleFromCollaborator({ state, dispatch, commit }, { collaboratorId, roleId }) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }

            commit('setLoading', { key: 'update', value: true });

            try {
                await service.removeCollaboratorRole(state.scope, collaboratorId, roleId);
                await dispatch('fetchCollaborators');
                await dispatch('fetchLimitsUsage');
            } catch (error) {
                throw error;
            } finally {
                commit('setLoading', { key: 'update', value: false });
            }
        },

        async exportFtpCredentials({ state }, { type, gallery_id }) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }

            try {
                await service.exportFtpCredentials(state.scope, { type, gallery_id });
            } catch (error) {
                throw error;
            }
        },

        // Virtual Users Actions
        async createVirtualUsers({ state, commit, dispatch }, data) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }

            try {
                const response = await service.createVirtualUsers(state.scope, data);
                // Response: {collaborators: [...], credentials: [...], errors: {...}, success_count: N, error_count: N}

                // Store credentials for modal display (array of credentials)
                if (response.credentials && response.credentials.length > 0) {
                    commit('setVirtualUserCredentials', response.credentials);
                }

                // Refresh collaborators list and limits
                await dispatch('fetchCollaborators');
                await dispatch('fetchLimitsUsage');

                return response;
            } catch (error) {
                throw error;
            }
        },

        async resetAllVirtualUserPasswords({ state, commit }, collaboratorIds = []) {
            if (!state.scope) {
                throw new Error('Scope not set');
            }

            try {
                const response = await service.resetAllVirtualUserPasswords(state.scope, collaboratorIds);

                // Store array of credentials for bulk display
                // Response format: {message, count, credentials: [...], errors: {...}}
                if (response.credentials) {
                    commit('setVirtualUserCredentials', response.credentials);
                }

                return response;
            } catch (error) {
                throw error;
            }
        },

        clearVirtualUserCredentials({ commit }) {
            commit('clearVirtualUserCredentials');
        },

        // Collaborator Limits Actions
        async fetchLimitsUsage({ commit, state }) {
            // Only available for pica-service scope
            if (!state.scope || state.scope.type !== 'pica-service') {
                commit('clearLimitsUsage');
                return null;
            }

            try {
                const data = await service.fetchLimitsUsage(state.scope);
                commit('setLimitsUsage', data);
                return data;
            } catch (error) {
                // If endpoint not available or error, clear limits
                commit('clearLimitsUsage');
                return null;
            }
        },

        clearLimitsUsage({ commit }) {
            commit('clearLimitsUsage');
        }
    }
});